import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useScreenData } from '../hooks/useScreenData';
import RecordList, { Pager, ViewSwitch } from '../components/RecordList';
import { EmptyState, InlineError, ReadError, LoadingState, StaleNotice } from '../components/PageStates';
import { formatUtc } from './auditFields';
import './Lists.css';

// server/schemas/platform-user.schemas.js: q of 3 to 255 characters, page, limit. Nothing else.
// A longer q is never sent: a refused query retried as-is fails forever (auditFields.js).
const MIN_QUERY = 3;
const MAX_QUERY = 255;
const LIMIT = 20;
const orNotSet = (value) => value || 'Not set';

/**
 * Every field the search returns (platformUser.service.js searchUsers), with the owning tenant,
 * except created_at, which the design does not ask for.
 */
const MEMBER_COLUMNS = [
  ['Member', (u) => <Link to={`/users/${u.id}`}>{u.username}</Link>],
  ['Member ID', (u) => <span className="record-id">{u.id}</span>],
  ['Name', (u) => orNotSet([u.first_name, u.last_name].filter(Boolean).join(' '))],
  ['Display name', (u) => orNotSet(u.display_name)],
  ['Email', (u) => orNotSet(u.email)],
  ['Tenant', (u) => (
    <>
      <Link to={`/tenants/${u.tenant_id}`}>{u.tenant_name}</Link> ({u.tenant_slug})
      {!u.tenant_active && <> <span className="badge badge-locked">Tenant locked</span></>}
    </>
  )],
  ['Role', (u) => u.role],
  ['Status', (u) => (u.is_active
    ? <span className="badge badge-active">Active</span>
    : <span className="badge badge-locked">Locked</span>)],
  ['Sign-in', (u) => u.auth_provider],
  ['Last login', (u) => (u.last_login ? <time dateTime={u.last_login}>{formatUtc(u.last_login)}</time> : 'Never')],
];

export default function UserSearch() {
  const [tableOnNarrow, setTableOnNarrow] = useState(false);
  const [text, setText] = useState('');
  const [query, setQuery] = useState({ q: '', page: 1 });
  const debounce = useRef(null);
  const ids = useId();
  useEffect(() => () => clearTimeout(debounce.current), []);

  const ready = query.q.length >= MIN_QUERY;
  const tooLong = query.q.length > MAX_QUERY;
  const params = { q: query.q, page: query.page, limit: LIMIT };
  // The query key holds the search, so an answer to an older search is never shown.
  const list = useScreenData(JSON.stringify(params), (options) => api.searchUsers(params, options),
    { enabled: ready && !tooLong, keeps: (data) => data.users.length > 0 });

  const onChange = (e) => {
    const { value } = e.target;
    setText(value);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQuery({ q: value.trim(), page: 1 }), 300);
  };

  const data = list.data;
  const page = data?.page ?? query.page;
  const pages = data ? Math.max(1, Math.ceil(data.total / (data.limit || LIMIT))) : 1;
  return (
    <div className="list-page">
      <h2>Users</h2>

      <form className="list-filters" aria-label="Member search" onSubmit={(e) => e.preventDefault()}>
        <div className="field">
          <label htmlFor={`${ids}q`}>Search members</label>
          <input id={`${ids}q`} type="search" placeholder="Search by username, email or display name"
            value={text} onChange={onChange} aria-describedby={`${ids}hint`} />
          <p id={`${ids}hint`} className="field-hint">Type at least 3 characters. Every household is searched.</p>
        </div>
      </form>

      {!ready && (
        <div className="page-state">
          <p className="page-state-title">Type at least 3 characters to search members in every household</p>
        </div>
      )}
      <ViewSwitch tableOnNarrow={tableOnNarrow} onChange={setTableOnNarrow} />
      {list.loading && <LoadingState label="Searching members" lines={6} />}
      {tooLong && <InlineError title="Check the search" message={`A search can be at most ${MAX_QUERY} characters.`} />}
      {list.error && <ReadError error={list.error} onRetry={list.reload} />}
      {list.stale && <StaleNotice loadedAt={list.loadedAt} reason={list.stale} onReload={list.reload} />}
      {/* The count and the page are two statements, so a page past the end can come back empty. */}
      {data && data.users.length === 0 && (data.total > 0
        ? <EmptyState title="No members on this page">This page is past the end of the results.</EmptyState>
        : <EmptyState title={`No members match “${query.q}”`} />)}
      {data && data.users.length > 0 && (
        <>
          <p className="list-summary"><span className="record-count">{data.total}</span> {data.total === 1 ? 'result' : 'results'} for “{query.q}”</p>
          <RecordList tableOnNarrow={tableOnNarrow} label="Members" caption={`Members matching “${query.q}”, every household`} columns={MEMBER_COLUMNS}
            records={data.users} recordKey={(u) => u.id} />
        </>
      )}
      {data && (data.users.length > 0 || pages > 1 || page > 1) && (
        <Pager page={page} pages={pages} disabled={Boolean(list.stale)} onPage={(next) => setQuery((q) => ({ ...q, page: next }))} />
      )}
    </div>
  );
}
